import { ComponentProps, CSSProperties, ReactNode } from "react";
import {
  WidgetCard as DsWidgetCard,
  WidgetCardActions,
  WidgetCardHeader as DsWidgetCardHeader,
  WidgetCardHeading,
  WidgetCardTitle
} from "@libs/zapi-ui/ds/components/widget-card";
import { useStuck } from "@/zapi-frontend/shared/hooks/useStuck";
import { cn } from "@/zapi-frontend/shared/lib/utils";

export function WidgetCard({ className, ...props }: ComponentProps<"article">) {
  return <DsWidgetCard className={cn("@container/widget min-w-0", className)} {...props} />;
}

const TITLE_OVERFLOW = { truncate: "truncate", clamp: "line-clamp-2", wrap: undefined };

type TWidgetCardHeaderProps = {
  title: ReactNode;
  titleIcon?: ReactNode;
  titleAdornment?: ReactNode;
  titleOverflow?: keyof typeof TITLE_OVERFLOW;
  actions?: ReactNode;
  stickyTop?: string;
  className?: string;
};

export function WidgetCardHeader({
  title,
  titleIcon,
  titleAdornment,
  titleOverflow = "truncate",
  actions,
  stickyTop,
  className
}: TWidgetCardHeaderProps) {
  const { ref, stuck } = useStuck<HTMLElement>(!!stickyTop);
  return (
    <DsWidgetCardHeader
      ref={ref}
      sticky={!!stickyTop}
      data-stuck={stuck || undefined}
      style={stickyTop ? ({ "--widget-sticky-top": stickyTop } as CSSProperties) : undefined}
      className={cn("flex-wrap gap-y-1", className)}
    >
      {stickyTop && (
        <span
          aria-hidden
          className="pointer-events-none absolute -inset-x-px bottom-full hidden h-(--widget-sticky-gap,0px) bg-canvas in-data-stuck:block"
        />
      )}
      <WidgetCardHeading>
        <WidgetCardTitle>
          {titleIcon}
          <span className={TITLE_OVERFLOW[titleOverflow]}>{title}</span>
          {titleAdornment}
        </WidgetCardTitle>
      </WidgetCardHeading>
      {actions && <WidgetCardActions className="flex-wrap">{actions}</WidgetCardActions>}
    </DsWidgetCardHeader>
  );
}

export function WidgetTiles({ children }: { children: ReactNode }) {
  return (
    <div className="flex flex-wrap gap-px overflow-hidden rounded-xl bg-border ring-1 ring-border *:min-w-[170px] *:flex-1">
      {children}
    </div>
  );
}

export function WidgetCardSeparator() {
  return <span aria-hidden className="mx-1 h-5 w-px self-center bg-foreground/10" />;
}
